import { Suspense, type ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useReducedMotion } from 'motion/react';
import { trpc } from '@/lib/trpc';
import { Image } from '@/routes/-components/image';
import { diamondGlows, DiamondGrid } from '../primitives';
import { LottiePlayer } from '../lottie/lottie-player';
import mailboxAnimation from '../lottie/contact-mailbox.json';
import { CallIcon, LocationIcon } from './icons';
import type { Audience } from './panel';

/** The office, as the hand-off writes it. `www/contact` still says 108 — 112 is the number to keep. */
const ADDRESS_LINES = ['Carrer de Llull, 112, Sant Martí, 08005', 'Barcelona, España.'];
const MAPS_URL = 'https://maps.google.com/?q=Carrer+de+Llull+112+08005+Barcelona';
/** Human form for the label, E.164 for the link. */
const PHONE_DISPLAY = '(+34) 936 11 01 04';
const PHONE_HREF = 'tel:+34936110104';

/**
 * The hand-off's dotted areas are all the same 777x501 ellipse of 4.39px diamonds on a 27.59px pitch,
 * which is what `DiamondGrid` models — so the exports are not shipped, the primitive redraws them.
 * Both numbers are measured off this page's own export, not rounded to the homepage's 5/27.
 */
const DIAMOND_TILE = { size: 4.39, gap: 27.59 } as const;

/** As many as the strip fits at the designed width without the logos shrinking to nothing. */
const LOGO_COUNT = 6;

/**
 * The strip between the photograph and the address card, and it answers the question the panel just
 * asked: a company sees the brands ALREADY doing this, everybody else sees the NGOs the money reaches.
 * NGOs are also what an undecided visitor sees, since that is what the hand-off draws.
 *
 * REAL entities either way — `partnerMerchants` and `socialOrgs` off the stats the homepage and
 * Solutions already render, so the strip can never name a partner we no longer work with. The hand-off
 * pictures ten hand-picked black lockups instead, and two of them (Amnesty, Greenpeace) are not
 * Worldcoo partners at all, which is the whole argument for reading the list we hold rather than
 * drawing one. Rendered greyscale and unboxed, which is a deliberate departure from the hand-off's
 * linen card — a partner's brand guidelines may well have something to say about it.
 *
 * Both lists arrive in the SAME query, so switching between them costs no request — and it fetches for
 * itself, so the Suspense boundary wraps the strip alone: the photograph above it and the address below
 * need no data and must not wait for one.
 */
function PartnerLogos({ audience }: { audience: Audience | null }) {
	const { t } = useLingui();
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	const isCompany = audience === 'company';
	const partners = isCompany ? stats.partnerMerchants : stats.socialOrgs;
	const logos = partners.filter((partner) => partner.logo).slice(0, LOGO_COUNT);
	if (logos.length === 0) return null;

	return (
		<ul
			aria-label={isCompany ? t`Partner company logos` : t`Partner NGO logos`}
			className="flex h-[104px] items-center justify-between gap-6 md:gap-10"
		>
			{logos.map((partner) => (
				<li key={partner.id} className="flex h-16 min-w-0 flex-1 items-center justify-center">
					{/* `grayscale` rather than a solid-black silhouette: these arrive as partner artwork of
					    unknown transparency, and `brightness-0` would paint a whole opaque plate black
					    instead of just the mark. Greyscale reads the same on every one of them. */}
					<Image
						src={partner.logo?.location}
						alt={partner.name ?? partner.id}
						height={64}
						aspectRatio={2}
						objectFit="contain"
						className="max-h-16 w-full object-contain! grayscale"
					/>
				</li>
			))}
		</ul>
	);
}

/** One "eyebrow + value" pair on the address card: a 16px glyph, its faint label, then the value. */
function ContactDetail({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
	return (
		<div className="relative flex flex-col gap-2">
			{/* The glyph sits ABOVE its label, not beside it — the hand-off stacks them with a 4px gap. */}
			<div className="text-ink flex flex-col gap-1 opacity-50">
				{icon}
				<span className="text-sm font-medium tracking-[-0.02em]">{label}</span>
			</div>
			{children}
		</div>
	);
}

/**
 * 369x358 starting at 51% of the card's width and 26px down — the hand-off's own placement, which runs
 * the post off the bottom edge, so the card's clipping is load-bearing. Kept in percentages of the card
 * so it scales with it instead of drifting off the corner. The aspect is stated rather than left to the
 * image's own, so the still and the player occupy the identical box.
 *
 * Hidden below `lg`: the address beside it wraps far wider than its designed 276px on a phone, and the
 * drawing would land on top of the text.
 */
const ILLUSTRATION_BOX =
	'pointer-events-none absolute top-[26px] left-[51%] hidden aspect-[370/358] w-[46.6%] lg:block';

/** "¿Quieres visitarnos?" — where we are and how to phone, on the pale olive card. */
function Whereabouts({ sent }: { sent: boolean }) {
	const { t } = useLingui();
	const reduce = useReducedMotion() ?? false;
	return (
		<div className="bg-sage relative flex flex-col gap-8 overflow-hidden rounded-3xl p-10 lg:h-[308px]">
			{/* One glow, centred at 50.5%/131% of the card: that is where the hand-off's ellipse sits, and
			    the three others it stacks here fall almost entirely outside the card. */}
			<DiamondGrid color="#35433F" opacity={1} {...DIAMOND_TILE} masks={[diamondGlows({ x: '50.5%', y: '131%' })]} />

			{/* Still until the message is away, then it draws itself once — the trim-path animation is
			    built from this very SVG's own paths, so the last frame and the still are the same drawing
			    and the swap cannot jump. `loop={false}`: it is a confirmation, not decoration.

			    Under `prefers-reduced-motion` the still stays. The player renders a paused first frame in
			    that case, and the first frame of a draw-on is an empty box. */}
			{sent && !reduce ? (
				<LottiePlayer
					animationData={mailboxAnimation}
					loop={false}
					ariaLabel={t`An envelope going into a letterbox`}
					className={ILLUSTRATION_BOX}
				/>
			) : (
				<img
					src="/redesign/contact/mailbox.svg"
					alt={t`An envelope going into a letterbox`}
					className={ILLUSTRATION_BOX}
				/>
			)}

			<ContactDetail icon={<LocationIcon className="size-4" />} label={t`Want to visit us?`}>
				<a
					href={MAPS_URL}
					target="_blank"
					rel="noopener noreferrer"
					className="hover:text-brand-strong max-w-[276px] leading-[1.5] transition-colors"
				>
					{ADDRESS_LINES.map((line) => (
						<span key={line} className="block">
							{line}
						</span>
					))}
				</a>
			</ContactDetail>

			<ContactDetail icon={<CallIcon className="size-4" />} label={t`You can also call us`}>
				<a href={PHONE_HREF} className="hover:text-brand-strong leading-[1.5] transition-colors">
					{PHONE_DISPLAY}
				</a>
			</ContactDetail>
		</div>
	);
}

/**
 * The Contact page's left column: a photograph, the partner-NGO strip, and the address card. 793 wide
 * against the panel's 559, with the hand-off's 16px gutters — not the 24px the rest of the site uses.
 *
 * Every block keeps the height it is drawn at (357 + 104 + 308 + two 16px gutters = the panel's 801),
 * and the column does NOT stretch. The hand-off has both columns ending together, but it only ever
 * draws an 801px panel: letting the photograph absorb the difference instead made it grow past 900px
 * as a visitor ticked their way through the company questionnaire, which reads as a bug.
 */
export function ContactInfo({ audience, sent }: { audience: Audience | null; sent: boolean }) {
	const { t } = useLingui();
	return (
		<div className="flex flex-col gap-4">
			<div className="relative aspect-[793/357] overflow-hidden rounded-3xl">
				<Image
					src="/redesign/contact/hero.jpg"
					alt={t`Children smiling outside their school`}
					height={682}
					aspectRatio={1024 / 682}
					objectFit="cover"
					priority
					className="absolute inset-0 size-full object-cover!"
				/>
			</div>
			<Suspense fallback={<div className="h-[104px]" />}>
				<PartnerLogos audience={audience} />
			</Suspense>
			<Whereabouts sent={sent} />
		</div>
	);
}
